/**
 * Build routes from Enhanced Navigation Structure
 *
 * Converts navigation from UserPermissionsCache into the route format
 * expected by the Sidenav component.
 *
 * @param {Object} navigation - Enhanced navigation structure with ResourceType sections
 * @param {Object} user - User object with display info
 * @param {string} userIcon - Icon to display for user profile
 * @returns {Array} Routes array for Sidenav component
 */

import React from 'react';
import Icon from "@mui/material/Icon";
import MDAvatar from "components/MDAvatar";
import { getComponentByPath } from './componentRegistry';

// User Profile components
import UserProfile from "pages/common/account/profile";
import InitialsAvatar from "pages/common/account/profile/components/InitialsAvatar";

/**
 * Convert icon string or component to Icon component
 */
function getIconComponent(icon) {
  if (!icon) {
    return <Icon fontSize="medium">folder</Icon>;
  }

  // If already a React component, return as-is
  if (React.isValidElement(icon)) {
    return icon;
  }

  // If string, create Icon component
  if (typeof icon === 'string') {
    return <Icon fontSize="medium">{icon}</Icon>;
  }

  // Fallback to folder icon
  return <Icon fontSize="medium">folder</Icon>;
}

/**
 * Build routes from enhanced navigation structure
 */
export default function buildRoutesFromNavigation(navigation, user, userIcon) {
  // If no navigation, return empty routes with user profile
  if (!navigation || !navigation.sections || navigation.sections.length === 0) {
    console.warn('No navigation data available, returning minimal routes');
    return buildMinimalRoutes(user, userIcon);
  }

  console.log('Building routes from navigation', {
    version: navigation.version,
    sectionCount: navigation.sections.length,
    generatedAt: navigation.generatedAt
  });

  const routes = [];

  // User profile section (always first)
  const displayName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.username;

  routes.push({
    type: 'collapse',
    name: displayName,
    key: displayName,
    route: '/user/profile',
    icon: <InitialsAvatar user={user} size='sm' />,
    component: <UserProfile />,
    noCollapse: true,
  });

  routes.push({ type: 'divider', key: 'divider-user' });

  // Build sections from navigation
  navigation.sections.forEach((section, sectionIndex) => {
    if (!section.items || section.items.length === 0) {
      console.warn('Section has no items, skipping:', section.name);
      return;
    }

    const sectionRoute = {
      type: 'collapse',
      name: section.name,
      key: `section-${section.id || sectionIndex}`,
      icon: getIconComponent(section.icon),
      collapse: []
    };

    // Add items to section
    section.items.forEach((item, itemIndex) => {
      const itemRoute = {
        name: item.label || item.resourceCode,
        key: `${sectionRoute.key}-item-${item.resourceId || itemIndex}`,
        route: item.route || `/resources/${item.resourceCode}`, // Use route from catalog, fallback to default pattern
        component: getComponentByPath(item.componentPath || item.resourceCode), // Use componentPath from catalog
        icon: getIconComponent(item.icon) // Add icon from catalog
      };

      // Add tooltip if available
      if (item.tooltip) {
        itemRoute.description = item.tooltip;
      }

      // Add domain badge if available
      if (item.domain) {
        itemRoute.domain = item.domain;
      }

      // Add metadata if available
      if (item.metadata) {
        itemRoute.metadata = item.metadata;
      }

      sectionRoute.collapse.push(itemRoute);
    });

    routes.push(sectionRoute);
  });

  console.log('Built routes from navigation', {
    routeCount: routes.length,
    sectionCount: routes.filter(r => r.type === 'collapse' && r.collapse).length
  });

  return routes;
}

/**
 * Build minimal routes when navigation is not available
 */
function buildMinimalRoutes(user, userIcon) {
  const displayName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.username;

  return [
    {
      type: 'collapse',
      name: displayName,
      key: displayName,
      route: '/user/profile',
      icon: <InitialsAvatar user={user} size='sm' />,
      component: <UserProfile />,
      noCollapse: true,
    },
    { type: 'divider', key: 'divider-user' },
    {
      type: 'collapse',
      name: 'Home',
      key: 'home',
      icon: <Icon fontSize="medium">home</Icon>,
      collapse: [
        {
          name: 'Dashboard',
          key: 'dashboard',
          route: '/dashboards/home',
          component: <div>Dashboard</div>
        }
      ]
    }
  ];
}

/**
 * Check if navigation is available and valid
 */
export function hasValidNavigation(permissions) {
  return !!(
    permissions?.navigation &&
    permissions.navigation.version === 5 &&
    permissions.navigation.sections &&
    Array.isArray(permissions.navigation.sections)
  );
}
